import React from 'react'
import { ElementProps } from '../base/element'
import { Item } from '../base/item'
import { useDragDrop } from '../helpers/useDragDrop'
import { IValueType, SchemaManager } from '../schemamanager/schemaManager'

import {
  IComponent,
  IDataComponent,
  IPropStyle,
  IPropStyles
} from '../schemamanager/types'
import { IDataTableActions, IDataTableComponent } from './datatable'
import {
  DataTableToolbarFunc,
  DetailComponentButtonsFunc,
  RowToolbarFunc
} from '../helpers/datatableItems'

export interface ElementTableProps extends ElementProps {
  m: IPropStyles
}

export const DataTableTable = (props: ElementTableProps) => {
  const { sm, m } = props
  const comp = props.comp as IDataTableComponent
  const options = comp.options || {}
  const fields = sm.getFieldsOfDataTable(comp)
  const isModal = comp.detailComponent.type === 'modal'

  const { val: data, memval: memdata } = sm.getValuesDataTable(comp)
  const keyFieldComp = sm.getCompByField(sm.getDataTableKeyField(comp))
  const showRowToolbar = (o: IDataTableActions): boolean =>
    !!o.add || !!o.copy || !!o.edit || !!o.delete
  const getRowCount = (): number =>
    showRowToolbar(options!.rowActions!) ? fields.length + 1 : fields.length

  let dtc = sm.getCompByName('detailcomponentbuttons')
  if (!dtc) {
    comp.detailComponent['children'].push(
      DetailComponentButtonsFunc(sm, comp) as IComponent
    )
  } else {
    const ind = (comp.detailComponent['children'] as Array<IComponent>).findIndex(c => c === dtc)
    if (ind > -1) {
      comp.detailComponent['children'][ind] = DetailComponentButtonsFunc(sm, comp) as IComponent
    }
  }

  const dnd = useDragDrop()
  if (comp.options?.dragdrop) {
    dnd.InitData(data, memdata)
  }

  const dndProps = (ind: number) =>
    comp.options?.dragdrop
      ? {
          draggable: true,
          onDragStart: dnd.onDragStart(ind),
          onDragOver: dnd.onDragOver,
          onDragEnter: dnd.onDragEnter(ind),
          onDrop: dnd.onDrop,
          onDragEnd: dnd.onDragEnd(dropped)
        }
      : {}

  const dropped = (dropdata: any, dropmemdata: any) => {
    if (dropdata) {
      sm.updateValue(comp, dropdata)
      sm.MemValues[comp.field!] = dropmemdata
      sm.RenderComponent(comp)
    }
  }

  const rowClick = (ind: number) => () => {
    if (options.rowSelecting) comp.selectedRowIndex = ind
    if (options!.editOnSingleClick) {
      if (ind === comp.editRowIndex) {
        sm.SaveChangesDataTable(comp)
      } else {
        if (!comp.editRowIndex || comp.editRowIndex === -1) {
          sm.EditDataTableRow(comp, ind)
        }
      }
    }
  }

  const rowDoubleClick = (ind: number) => () => {
    if (options.rowSelecting) comp.selectedRowIndex = ind
    if (options!.editOnDoubleClick) {
      if (ind === comp.editRowIndex) {
        sm.SaveChangesDataTable(comp)
      } else {
        if (!comp.editRowIndex || comp.editRowIndex === -1) {
          sm.EditDataTableRow(comp, ind)
        }
      }
    }
  }

  const getStyleTableRow = (ind: number): IPropStyle => {
    if (options.tableStriped) {
      if (ind % 2 === 0) {
        return {
          ...m.styleTableRow,
          ...m.styleTableRowEven
        }
      } else {
        return {
          ...m.styleTableRow,
          ...m.styleTableRowOdd
        }
      }
    } else {
      return m.styleTableRow
    }
  }

  const getStyleTableCell = (
    comp: IComponent,
    arrayInd: number
  ): IPropStyle => {
    if (sm.getDiffHighlight(comp, arrayInd)) {
      return {
        className: m.styleTableCell.className + ' ' + sm.DiffClassName,
        style: m.styleTableCell.style
      }
    } else {
      return m.styleTableCell
    }
  }

  const getHeader = () => {
    if (options.hideTableHeader) return null
    const h = fields.map((f) => {
      const key = sm.getObjectId(f as IComponent)
      return (
        <th key={key} {...m.styleTableHeaderCell}>
          {getSummaryTitle(f as IDataComponent)}
        </th>
      )
    })
    if (showRowToolbar(options!.rowActions!))
      h.push(<th key={99999} {...m.styleTableCell}></th>)
    return h
  }

  const getRows = () => {
    return (
      data &&
      keyFieldComp &&
      data.map((_: any, ind: number) => {
        const key = sm.getValue(keyFieldComp, ind)
        return (
          <React.Fragment key={key}>
            <tr
              {...dndProps(ind)}
              tabIndex={0}
              onClick={rowClick(ind)}
              onDoubleClick={rowDoubleClick(ind)}
              {...getStyleTableRow(ind)}
            >
              {getCells(ind)}
              {getRowToolbar(ind)}
            </tr>
            {!isModal && comp.editRowIndex === ind && (
              <tr>
                <td colSpan={getRowCount()}>
                  <Item sm={sm} comp={comp.detailComponent as IComponent} />
                </td>
              </tr>
            )}
          </React.Fragment>
        )
      })
    )
  }

  const getCells = (ind: number) => {
    return fields.map((field) => {
      const f = field as IDataComponent
      const key = f.field
      return (
        <td key={key} {...getStyleTableCell(f as IComponent, ind)}>
          {getSummaryCell(f, ind)}
        </td>
      )
    })
  }

  const getRowToolbar = (ind: number) => {
    if (!showRowToolbar(options!.rowActions!)) return null
    const rowtoolbar = RowToolbarFunc(sm, comp, ind)
    return (
      <td {...m.styleTableCell}>
        <div className='flex justify-end'>
        <Item sm={sm} comp={rowtoolbar}></Item>
        </div>
      </td>
    )
  }

  const getDefaultTitle = (field: IDataComponent) => {
    if (field.parentComp && field.parentComp.type == 'field') {
      return field.parentComp.label
    } else {
      if (field['placeholder']) return field['placeholder']
      return field.field
    }
  }

  const getSummaryTitle = (field: IDataComponent) => {
    if (comp.summaryCellTitle) {
      const res = comp.summaryCellTitle({ sm, comp: field as IComponent })
      const typ = SchemaManager.checkValueType(res)
      if (typ === IValueType.component) {
        return <Item {...props} comp={res}></Item>
      } else {
        if (typ !== IValueType.undefined) {
          return <span>{res}</span>
        } else {
          return getDefaultTitle(field)
        }
      }
    } else {
      return getDefaultTitle(field)
    }
  }

  const getSummaryCell = (field: IDataComponent, arrayInd: number) => {
    const value = sm.getValue(field as IComponent, arrayInd)
    if (comp.summaryCellValue) {
      const res = comp.summaryCellValue({
        sm,
        comp: field as IComponent,
        value,
        arrayInd
      })
      const typ = SchemaManager.checkValueType(res)
      if (typ === IValueType.component) {
        return <Item {...props} comp={res}></Item>
      } else {
        if (typ !== IValueType.undefined) {
          return <span>{res}</span>
        } else {
          return value.toString()
        }
      }
    } else {
      return value.toString()
    }
  }

  const showToolbar = (o: IDataTableActions): boolean =>
    !!o.add || !!o.copy || !!o.edit || !!o.delete || !!comp.label
  const toolbar = DataTableToolbarFunc(sm, comp)

  return (
    <div {...m.styleTableContent}>
      <table {...m.styleTable}>
        <thead {...m.styleTableHead}>
          {showToolbar(comp.options!.toolbarActions!) && (
            <tr {...m.styleHeader}> 
              <td colSpan={getRowCount()}>
                <Item sm={sm} comp={toolbar}></Item>
              </td>
            </tr>
          )}

          <tr {...m.styleTableHeadRow}>{getHeader()}</tr>
        </thead>
        <tbody {...m.styleTableBody}>{getRows()}</tbody>
      </table>
    </div>
  )
}
